'use es6';

import PropTypes from 'prop-types';
import { useContext, useEffect } from 'react';
import { connect } from 'react-redux';
import { DragSource } from 'react-dnd-compat';
import { getEmptyImage } from 'react-dnd-ipeui-backend';
import { makeGetCellOrRowParentId, getLayoutSectionTreeById } from 'ContentEditorUI/redux/selectors/moduleSelectors';
import { dragTypes } from 'InpageEditorUI/lib/layoutSection/constants';
import { draggingStartedAndDeselectExisting as draggingStartedAndDeselectExistingAction, draggingStoppedAndReselectIfNeeded as draggingStoppedAndReselectIfNeededAction } from 'InpageEditorUI/redux/actions/layoutSectionDndActions';
import { getShouldUseCustomSections } from 'InpageEditorUI/redux/selectors/layoutSectionDndSelectors';
import { buildActionsForSection, BaseResponsiveSectionToolbar } from './BaseResponsiveSectionToolbar';
import { DEFAULT_RESPONSIVE_SIZE } from 'layout-dnd-components/DndToolbar/constants';
import { getIsInDefaultBreakpointMode } from 'ContentEditorUI/redux/selectors/styleBreakpointsSelectors';
import TourContext from 'ui-shepherd-react/contexts/TourContext';
import { getIsInDeveloperMode } from 'ContentEditorUI/redux/selectors/appStatusSelectors';
import { getSelectedItemId } from 'ContentEditorUI/redux/selectors/selectionSelectors';
import { useToggleNodeVisibilityOnThisBreakpoint, getRowById, getGridSectionById } from 'ContentEditorUI/components/sidebar/contentTree/utils';
import { getIsPatagoniaSidebarCollapsed } from 'ContentEditorUI/redux/selectors/sidebarSelectors';
import { getIsUngatedForChangeSectionLayout } from 'ContentEditorUI/redux/selectors/authSelectors';
import { getTargetedGridTree } from 'InpageEditorUI/redux/selectors/layoutSectionDndSelectors';
import { jsx as _jsx } from "react/jsx-runtime";
const dragSource = {
  beginDrag(props) {
    const {
      id,
      parentId,
      draggingStartedAndDeselectExisting,
      layoutSectionId,
      gridLayoutId
    } = props;
    const dragItem = {
      dragId: id,
      dragParentId: parentId,
      isColumn: false,
      isRow: true,
      isTopMostRow: true,
      originLayoutSectionId: layoutSectionId || gridLayoutId,
      dragType: dragTypes.EXISTING_ITEM
    };
    draggingStartedAndDeselectExisting(dragItem);
    return dragItem;
  },
  canDrag(props) {
    return props.isInDefaultBreakpointMode;
  },
  endDrag(props, monitor) {
    const {
      draggingStoppedAndReselectIfNeeded
    } = props;
    draggingStoppedAndReselectIfNeeded(monitor.getItem(), monitor.didDrop());
  }
};
function dragCollect(monitor) {
  return {
    isDragging: monitor.isDragging()
  };
}
const mapStateToProps = () => {
  const getCellOrRowParentId = makeGetCellOrRowParentId();
  return (state, ownProps) => {
    if (ownProps.id) {
      return {
        parentId: getCellOrRowParentId(state, {
          layoutSectionId: ownProps.layoutSectionId,
          id: ownProps.id
        }),
        isInDefaultBreakpointMode: getIsInDefaultBreakpointMode(state),
        shouldUseCustomSections: getShouldUseCustomSections(state),
        isInDeveloperMode: getIsInDeveloperMode(state),
        selectedItemId: getSelectedItemId(state),
        layoutSectionTree: getLayoutSectionTreeById(state, {
          layoutSectionId: ownProps.layoutSectionId
        }),
        gridLayoutTree: getTargetedGridTree(state),
        isPatagoniaSidebarCollapsed: getIsPatagoniaSidebarCollapsed(state),
        hasChangeSectionLayoutAccess: getIsUngatedForChangeSectionLayout(state)
      };
    }
    return {};
  };
};
const mapDispatchToProps = {
  draggingStartedAndDeselectExisting: draggingStartedAndDeselectExistingAction,
  draggingStoppedAndReselectIfNeeded: draggingStoppedAndReselectIfNeededAction
};
const ResponsiveSectionToolbar = ({
  id,
  layoutSectionId,
  gridLayoutId,
  active,
  top,
  left,
  onHandleMouseDown,
  onDropdownOpenChange,
  responsiveSize = DEFAULT_RESPONSIVE_SIZE,
  connectDragPreview,
  connectDragSource,
  dispatch,
  bus,
  isInDefaultBreakpointMode,
  shouldUseCustomSections,
  isInDeveloperMode,
  selectedItemId,
  layoutSectionTree,
  gridLayoutTree,
  isPatagoniaSidebarCollapsed,
  isCommentable,
  hasChangeSectionLayoutAccess
}) => {
  const {
    tour
  } = useContext(TourContext);
  const isStyleSectionStep = tour.getStep() === 'styleSection';
  const isSectionDropdownStep = tour.getStep() === 'sectionDropdown';
  const layoutSectionTreeNode = layoutSectionTree ? getRowById(layoutSectionTree, id) : null;
  const gridLayoutTreeNode = gridLayoutTree ? getGridSectionById(gridLayoutTree, id) : null;
  const toggleNodeVisibilityOnThisBreakpoint = useToggleNodeVisibilityOnThisBreakpoint(gridLayoutTreeNode || layoutSectionTreeNode, Boolean(gridLayoutTreeNode));
  const isEmbed = layoutSectionTreeNode && layoutSectionTreeNode.isEmbed();
  const shouldClosePatagoniaSidebar = !isPatagoniaSidebarCollapsed;
  useEffect(() => {
    connectDragPreview(getEmptyImage(), {
      captureDraggingState: true
    });
  }, [connectDragPreview]);
  const isStaticSection = Boolean(layoutSectionTreeNode) && layoutSectionTreeNode.isStaticSection();
  return /*#__PURE__*/_jsx(BaseResponsiveSectionToolbar, {
    active: active,
    actions: buildActionsForSection({
      sectionName: id,
      layoutSectionId,
      gridLayoutId,
      isInDefaultBreakpointMode,
      isStyleSectionStep,
      dispatch,
      bus,
      shouldUseCustomSections,
      isInDeveloperMode,
      toggleNodeVisibilityOnThisBreakpoint,
      shouldClosePatagoniaSidebar,
      isEmbed,
      isStaticSection,
      isCommentable,
      hasChangeSectionLayoutAccess
    }),
    insetBlockStart: `${top}px`,
    insetInlineStart: `${left}px`,
    responsiveSize: responsiveSize,
    onHandleMouseDown: onHandleMouseDown,
    onDropdownOpenChange: onDropdownOpenChange,
    connectDragSource: connectDragSource,
    isSectionDropdownStep: isSectionDropdownStep,
    isSelectedItem: id === selectedItemId,
    isEmbed: isEmbed,
    isCommentable: isCommentable,
    sectionId: id,
    isStaticSection: isStaticSection
  });
};
ResponsiveSectionToolbar.propTypes = {
  id: PropTypes.string,
  layoutSectionId: PropTypes.string,
  active: PropTypes.bool.isRequired,
  top: PropTypes.number.isRequired,
  left: PropTypes.number.isRequired,
  onHandleMouseDown: PropTypes.func,
  onDropdownOpenChange: PropTypes.func,
  responsiveSize: PropTypes.string,
  // eslint-disable-next-line react/no-unused-prop-types
  draggingStartedAndDeselectExisting: PropTypes.func.isRequired,
  // eslint-disable-next-line react/no-unused-prop-types
  draggingStoppedAndReselectIfNeeded: PropTypes.func.isRequired,
  connectDragPreview: PropTypes.func.isRequired,
  connectDragSource: PropTypes.func.isRequired,
  dispatch: PropTypes.func.isRequired,
  bus: PropTypes.object.isRequired,
  isInDefaultBreakpointMode: PropTypes.bool,
  shouldUseCustomSections: PropTypes.bool,
  isInDeveloperMode: PropTypes.bool,
  selectedItemId: PropTypes.string,
  layoutSectionTree: PropTypes.object,
  isPatagoniaSidebarCollapsed: PropTypes.bool,
  isCommentable: PropTypes.bool,
  isUngatedForChangeSectionLayout: PropTypes.bool
};
export default connect(mapStateToProps, mapDispatchToProps)(DragSource(dragTypes.EXISTING_ITEM, dragSource, dragCollect)(ResponsiveSectionToolbar));